/**
 * BreadthChart.jsx – Multi-pane via separate chart instances (LW v3)
 * ───────────────────────────────────────────────────────────────────
 * THE ALIGNMENT FIX (V3 COMPATIBLE):
 *   Because v3 doesn't support fixed price-scale widths, different text 
 *   lengths (e.g., '24430.35' vs '54.0%') cause differing plot area widths.
 *   
 *   To perfectly align them, we pad the indicator percentages with 
 *   Figure Spaces (\u2007) so they measure exactly as wide as the OHLC prices.
 */

import React, { useEffect, useRef, useCallback } from "react";
import { createChart } from "lightweight-charts";

export const INDICATORS = [
  { key: "breadth_200dma_pct",   label: "% above 200DMA",  color: "#2563eb" },
  { key: "breadth_50dma_pct",    label: "% above 50DMA",   color: "#7c3aed" },
  { key: "breadth_10dma_pct",    label: "% above 10DMA",   color: "#ca8a04" },
  { key: "breadth_blue_bar_pct", label: "% Blue Bars",      color: "#0891b2" },
  { key: "breadth_red_bar_pct",  label: "% Red Bars",       color: "#e11d48" },
  { key: "breadth_hhpc_pct",     label: "% above HHPC",    color: "#16a34a" },
  { key: "breadth_llpc_pct",     label: "% below LLPC",    color: "#f97316" },
];

const OHLC_PANE_HEIGHT    = 300;
const BREADTH_PANE_HEIGHT = 130;
const COMPACT_PANE_HEIGHT = 80;

// Formatters to ensure identical string lengths (8 chars)
const formatOHLC = (p) => {
  const str = p.toFixed(2);
  return str.length < 8 ? '\u2007'.repeat(8 - str.length) + str : str;
};

const formatPct = (p) => {
  const str = p.toFixed(1) + "%";
  return str.length < 8 ? '\u2007'.repeat(8 - str.length) + str : str;
};

// Shared chart options
function makeOpts(showGrid, showCrosshair, barSpacing, theme) {
  const isDark = theme === "dark";
  return {
    layout: {
      backgroundColor: isDark ? "#09090b" : "#ffffff",
      textColor:       isDark ? "#f8fafc" : "#111827",
    },
    grid: {
      vertLines: { color: showGrid ? (isDark ? "#27272a" : "#e5e7eb") : "transparent" },
      horzLines: { color: showGrid ? (isDark ? "#27272a" : "#e5e7eb") : "transparent" },
    },
    crosshair: {
      mode: showCrosshair ? 1 : 0,
      vertLine: { color: "transparent", width: 1, style: 1, labelBackgroundColor: isDark ? "#27272a" : "#f9fafb" },
      horzLine: { color: isDark ? "#6b7280" : "#9ca3af", width: 1, style: 1, labelBackgroundColor: isDark ? "#27272a" : "#f9fafb" },
    },
    rightPriceScale: { 
      visible: true, 
      borderColor: isDark ? "#27272a" : "#d1d5db" 
    },
    timeScale: {
      borderColor:  isDark ? "#27272a" : "#d1d5db",
      barSpacing,
      rightOffset:  5,
      fixLeftEdge:  false,
      fixRightEdge: false,
    },
    handleScroll: true,
    handleScale:  true,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────
const BreadthChart = React.forwardRef(function BreadthChart(
  {
    breadthData,
    visibleSeries,
    timeframe,
    range,
    barSpacing     = 7,
    showCrosshair  = true,
    showGrid       = true,
    showPaneLabels = true,
    compact        = false,
    indexLabel     = "NIFTY",
    theme          = "dark",
    onChartReady,
  },
  ref
) {
  const containerRef = useRef(null);
  const chartsRef    = useRef([]);   // [{ chart, series, key, data, dotDiv? }]
  const syncingRef   = useRef(false);
  const crosshairRef = useRef(null);

  // ── Build data ─────────────────────────────────────────────────────────────
  const buildSeries = useCallback(() => {
    if (!breadthData?.dates?.length) return null;
    const validSet = new Set();
    const candles  = [];
    breadthData.dates.forEach((d, i) => {
      const o = breadthData.ohlc?.[i];
      if (o && o.o > 0 && o.h > 0 && o.l > 0 && o.c > 0 &&
          !(o.o === o.h && o.h === o.l && o.l === o.c)) {
        validSet.add(d);
        candles.push({ time: d, open: o.o, high: o.h, low: o.l, close: o.c });
      }
    });
    const lineData = {};
    INDICATORS.forEach(ind => {
      const vals = breadthData.indicators?.[ind.key];
      if (!vals) return;
      const pts = [];
      breadthData.dates.forEach((d, i) => {
        if (!validSet.has(d)) return;
        const v = vals[i];
        pts.push(v != null && !isNaN(v) ? { time: d, value: v } : { time: d });
      });
      lineData[ind.key] = pts;
    });
    return { candles, lineData };
  }, [breadthData]);

  // ── Sync time scales ───────────────────────────────────────────────────────
  const syncScales = useCallback((src) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    try {
      const tr = src.timeScale().getVisibleLogicalRange();
      if (!tr) return;
      chartsRef.current.forEach(({ chart }) => {
        if (chart !== src) chart.timeScale().setVisibleLogicalRange(tr);
      });
    } finally { syncingRef.current = false; }
  }, []);

  // ── Main effect ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || !breadthData) return;

    chartsRef.current.forEach(({ chart }) => { try { chart.remove(); } catch (_) {} });
    chartsRef.current = [];
    containerRef.current.innerHTML = "";

    const seriesData  = buildSeries();
    if (!seriesData) return;

    const activePanes  = INDICATORS.filter(i => visibleSeries.has(i.key));
    const opts         = makeOpts(showGrid, showCrosshair, barSpacing, theme);
    const paneH        = compact ? COMPACT_PANE_HEIGHT : BREADTH_PANE_HEIGHT;
    const isDark       = theme === "dark";
    const borderColor  = isDark ? "#27272a" : "#e5e7eb";
    const OHLC_H       = OHLC_PANE_HEIGHT + 30; // +30 for time axis bar

    // ── OHLC pane ─────────────────────────────────────────────────────────
    const ohlcDiv = document.createElement("div");
    ohlcDiv.style.cssText = `width:100%;height:${OHLC_H}px;position:relative;border-bottom:1px solid ${borderColor};`;
    containerRef.current.appendChild(ohlcDiv);

    const ohlcChart = createChart(ohlcDiv, {
      ...opts,
      width:     ohlcDiv.clientWidth,
      height:    OHLC_H,
      timeScale: { ...opts.timeScale, visible: true },
    });

    const candleSeries = ohlcChart.addCandlestickSeries({
      upColor:          "#15803d",
      downColor:        "#b91c1c",
      borderUpColor:    "#15803d",
      borderDownColor:  "#b91c1c",
      wickUpColor:      "#15803d",
      wickDownColor:    "#b91c1c",
      priceLineVisible: false,
      lastValueVisible: true,
      priceFormat: {
        type: 'custom',
        formatter: formatOHLC,
        minMove: 0.05,
      }
    });
    candleSeries.setData(seriesData.candles);

    if (showPaneLabels) {
      const lbl = document.createElement("div");
      lbl.className = "bc-pane-label";
      lbl.textContent = indexLabel;
      ohlcDiv.appendChild(lbl);
    }

    // Crosshair handler 
    const handleCrosshairMove = (param) => {
      if (!crosshairRef.current) return;
      if (!param.time) {
        crosshairRef.current.style.display = "none";
        chartsRef.current.forEach(c => { if (c.dotDiv) c.dotDiv.style.display = "none"; });
        return;
      }
      const x = ohlcChart.timeScale().timeToCoordinate(param.time);
      if (x === null) {
        crosshairRef.current.style.display = "none";
        chartsRef.current.forEach(c => { if (c.dotDiv) c.dotDiv.style.display = "none"; });
        return;
      }
      crosshairRef.current.style.display = "block";
      crosshairRef.current.style.left    = `${x}px`;

      chartsRef.current.forEach(c => {
        if (!c.dotDiv || !c.data) return;
        const pt = c.data.find(p => p.time === param.time);
        if (pt && pt.value != null && !isNaN(pt.value)) {
          const y = c.series.priceToCoordinate(pt.value);
          if (y !== null) {
            Object.assign(c.dotDiv.style, { display: "block", left: `${x}px`, top: `${y}px` });
          } else { c.dotDiv.style.display = "none"; }
        } else { c.dotDiv.style.display = "none"; }
      });
    };

    chartsRef.current.push({ chart: ohlcChart, series: candleSeries, key: "_ohlc" });
    ohlcChart.timeScale().subscribeVisibleLogicalRangeChange(() => syncScales(ohlcChart));
    ohlcChart.subscribeCrosshairMove(handleCrosshairMove);

    // ── Indicator panes ───────────────────────────────────────────────────
    activePanes.forEach((ind, idx) => {
      const isLast = idx === activePanes.length - 1;
      const paneDiv = document.createElement("div");
      paneDiv.style.cssText = `width:100%;height:${paneH}px;position:relative;${!isLast ? `border-bottom:1px solid ${borderColor};` : ""}`;
      containerRef.current.appendChild(paneDiv);

      const paneChart = createChart(paneDiv, {
        ...opts,
        width:  paneDiv.clientWidth,
        height: paneH,
        timeScale: { ...opts.timeScale, visible: false },
      });

      const lineSeries = paneChart.addLineSeries({
        color:                  ind.color,
        lineWidth:              2,
        priceLineVisible:       false,
        lastValueVisible:       true,
        crosshairMarkerVisible: false,
        priceFormat: {
          type: 'custom',
          formatter: formatPct,
          minMove: 0.1,
        }
      });

      const data = seriesData.lineData[ind.key] ?? [];
      lineSeries.setData(data);

      paneChart.priceScale("right").applyOptions({
        autoScale: false,
        minValue: 0,
        maxValue: 100,
      });

      if (showPaneLabels) {
        const lbl = document.createElement("div");
        lbl.className = "bc-pane-label";
        lbl.textContent = ind.label;
        lbl.style.color = ind.color;
        paneDiv.appendChild(lbl);
      }

      // Hover dot
      const dotDiv = document.createElement("div");
      Object.assign(dotDiv.style, {
        position:        "absolute",
        width:           "10px",
        height:          "10px",
        borderRadius:    "50%",
        backgroundColor: ind.color,
        border:          `2px solid ${isDark ? "#09090b" : "#ffffff"}`,
        transform:       "translate(-50%,-50%)",
        zIndex:          "60",
        pointerEvents:   "none",
        display:         "none",
      });
      paneDiv.appendChild(dotDiv);

      chartsRef.current.push({ chart: paneChart, series: lineSeries, key: ind.key, dotDiv, data });
      paneChart.timeScale().subscribeVisibleLogicalRangeChange(() => syncScales(paneChart));
      paneChart.subscribeCrosshairMove(handleCrosshairMove);
    });

    // Fit & range
    chartsRef.current.forEach(({ chart }) => chart.timeScale().fitContent());
    applyRange(range, chartsRef.current[0]?.chart);

    // onChartReady
    if (typeof onChartReady === "function") {
      onChartReady({
        fit:      () => chartsRef.current.forEach(({ chart }) => chart.timeScale().fitContent()),
        goLatest: () => chartsRef.current.forEach(({ chart }) => chart.timeScale().scrollToRealTime()),
        panLeft:  () => chartsRef.current.forEach(({ chart }) => {
          const ts = chart.timeScale(); ts.scrollToPosition(ts.scrollPosition() - 30, true);
        }),
        panRight: () => chartsRef.current.forEach(({ chart }) => {
          const ts = chart.timeScale(); ts.scrollToPosition(ts.scrollPosition() + 30, true);
        }),
        zoomIn:  () => chartsRef.current.forEach(({ chart }) =>
          chart.timeScale().applyOptions({ barSpacing: Math.min(chart.timeScale().options().barSpacing + 2, 40) })
        ),
        zoomOut: () => chartsRef.current.forEach(({ chart }) =>
          chart.timeScale().applyOptions({ barSpacing: Math.max(chart.timeScale().options().barSpacing - 2, 2) })
        ),
        setBarSpacing: v => chartsRef.current.forEach(({ chart }) =>
          chart.timeScale().applyOptions({ barSpacing: v })
        ),
        setRange: r => applyRange(r, chartsRef.current[0]?.chart),
        toggleGrid: on => {
          const c = { color: on ? (isDark ? "#27272a" : "#e5e7eb") : "transparent" };
          chartsRef.current.forEach(({ chart }) =>
            chart.applyOptions({ grid: { vertLines: c, horzLines: c } })
          );
        },
        toggleCrosshair: on => chartsRef.current.forEach(({ chart }) =>
          chart.applyOptions({ crosshair: { mode: on ? 1 : 0 } })
        ),
        downloadPng: () => {
          if (!chartsRef.current[0]) return;
          try {
            const canvas = chartsRef.current[0].chart.takeScreenshot();
            const a = document.createElement("a");
            a.href = canvas.toDataURL("image/png");
            a.download = `breadth_${indexLabel.replace(/\s+/g, "_")}.png`;
            a.click();
          } catch (e) { console.warn("PNG:", e); }
        },
      });
    }

    if (ref) ref.current = { charts: chartsRef.current };

    // Resize observer
    const ro = new ResizeObserver(() => {
      chartsRef.current.forEach(({ chart }, i) => {
        const div = containerRef.current?.children[i];
        if (div) chart.resize(div.clientWidth, div.clientHeight);
      });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chartsRef.current.forEach(({ chart }) => { try { chart.remove(); } catch (_) {} });
      chartsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadthData, visibleSeries, showGrid, showCrosshair, showPaneLabels, compact, barSpacing, indexLabel, theme]);

  const isDark      = theme === "dark";
  const borderColor = isDark ? "#27272a" : "#e5e7eb";

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        ref={containerRef}
        style={{
          width:      "100%",
          border:     `1px solid ${borderColor}`,
          background: isDark ? "#09090b" : "#ffffff",
        }}
      />
      {/* Global vertical crosshair spanning all panes */}
      <div
        ref={crosshairRef}
        style={{
          position:      "absolute",
          top:           0,
          bottom:        0,
          width:         "1px",
          borderLeft:    "1px dashed #6b7280",
          zIndex:        50,
          pointerEvents: "none",
          display:       "none",
        }}
      />
    </div>
  );
});

// ── applyRange helper ────────────────────────────────────────────────────────
function applyRange(range, chart) {
  if (!chart || range === "ALL") { chart?.timeScale().fitContent(); return; }
  const now  = new Date();
  let from   = new Date(now);
  if (range === "YTD") {
    from = new Date(now.getFullYear(), 0, 1);
  } else {
    const months = { "6M": 6, "1Y": 12, "3Y": 36, "5Y": 60, "10Y": 120 };
    from.setMonth(from.getMonth() - (months[range] || 12));
  }
  try {
    chart.timeScale().setVisibleRange({
      from: from.toISOString().slice(0, 10),
      to:   now.toISOString().slice(0, 10),
    });
  } catch (_) { chart.timeScale().fitContent(); }
}

export default BreadthChart;
